import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { toast } from "sonner";
import { ChatMemberList } from "Components/Chat/ChatMemberList";
import { RenameChannelDialog } from "Components/Chat/RenameChannelDialog";
import { AddMemberDialog } from "Components/Chat/AddMemberDialog";
import type { SelectedUser } from "Components/Chat/ChatSelectedMembers";
import { NewGroupChatDialog } from "Components/Chat/NewGroupChatDialog";
import {
  getChatMemberProfileUrl,
  identityUserPublicIdFromStreamId,
} from "Components/Chat/chatConstants";
import { Link } from "Navigation/Link";
import { useRemoveChatChannelMemberMutation } from "Api/Mutations/Chat/useRemoveChatChannelMemberMutation";
import { useChatChannelDisplayInfo } from "Hooks/Chat/useChatChannelDisplayInfo";
import { useOpenCreatedChannel } from "Hooks/Chat/useOpenCreatedChannel";
import { Resources, useResource } from "Translations/Resources";

const MEMBERS_RESOURCES = Resources.Chat.Members;
const RENAME_RESOURCES = Resources.Chat.Rename;
const NEW_CHAT_RESOURCES = Resources.Chat.NewChat;

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Opens "Find in conversation"; the header swaps to the search bar. */
  onFindInConversation: () => void;
  channelId: string;
};

export const ChatChannelActions = ({
  anchorEl,
  onClose,
  onFindInConversation,
  channelId,
}: Props) => {
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const { t } = useResource();

  const { channel } = useChannelStateContext();
  const { setActiveChannel } = useChatContext();

  const currentUserId = channel.state.membership?.user?.id;
  const isCreator = channel.data?.created_by?.id === currentUserId;

  const { isGroup, displayName, otherMembers, isAnonymized } =
    useChatChannelDisplayInfo(channel, currentUserId);

  const openCreatedChannel = useOpenCreatedChannel();

  // Prefill rename with the STORED channel name only — never the computed display name, which
  // falls back to the joined member names and would persist "Alice, Bob" as the real name.
  const storedChannelName =
    typeof channel.data?.name === "string" ? channel.data.name : "";

  const { mutate: leaveChannel, isPending: isLeaving } =
    useRemoveChatChannelMemberMutation({
      onSuccess: () => {
        setIsLeaveConfirmOpen(false);
        setActiveChannel(undefined);
      },
      onError: () => toast.error(t(MEMBERS_RESOURCES.LeaveError)),
    });

  const handleMembersOpen = () => {
    onClose();
    setIsMembersOpen(true);
  };

  const handleRenameOpen = () => {
    onClose();
    setIsRenameOpen(true);
  };

  const handleAddMemberOpen = () => {
    onClose();
    setIsAddMemberOpen(true);
  };

  const handleCreateGroupOpen = () => {
    onClose();
    setIsCreateGroupOpen(true);
  };

  const handleLeaveOpen = () => {
    onClose();
    setIsLeaveConfirmOpen(true);
  };

  const handleLeaveConfirm = () => {
    if (!currentUserId) {
      return;
    }

    leaveChannel({
      channelId,
      memberIdentityUserPublicID:
        identityUserPublicIdFromStreamId(currentUserId),
    });
  };

  if (!isGroup) {
    const counterpartStreamId = otherMembers[0]?.user_id;

    if (!counterpartStreamId) {
      return null;
    }

    const counterpartUser = otherMembers[0]?.user;

    // The counterpart starts pre-selected, so starting a group from a 1:1 only costs picking the
    // people to add. The direct channel itself is left alone — this creates a NEW group channel.
    const groupSeedMembers: Array<SelectedUser> = [
      {
        identityUserPublicID:
          identityUserPublicIdFromStreamId(counterpartStreamId),
        name: displayName,
        // Already resolved by Stream (and blanked for an anonymised channel), so it goes
        // through as-is rather than being rebuilt from a file name.
        avatarUrl: counterpartUser?.image,
        accessRoleCode: counterpartUser?.accessRole,
        // Mirrored onto the Stream profile by the API (App ADR-0013); undefined on a
        // profile written before the field existed, which the dialog treats as unknown.
        isFullMoone: counterpartUser?.isFullMoone,
      },
    ];

    return (
      <>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
          <MenuItem onClick={onFindInConversation}>
            <ListItemIcon>
              <SearchIcon />
            </ListItemIcon>
            <ListItemText>
              {t(Resources.Chat.FindInConversation.MenuItem)}
            </ListItemText>
          </MenuItem>
          {/* A real link, so the profile can be opened in a new tab like any other; withheld on an
              anonymised channel, where the counterpart has no disclosable identity (ADR-0008). */}
          {!isAnonymized && (
            <MenuItem
              component={Link}
              to={getChatMemberProfileUrl(counterpartStreamId)}
              onClick={onClose}
            >
              <ListItemIcon>
                <PersonOutlineIcon />
              </ListItemIcon>
              <ListItemText>{t(Resources.Common.ShowProfile)}</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleCreateGroupOpen}>
            <ListItemIcon>
              <GroupAddIcon />
            </ListItemIcon>
            <ListItemText>
              {t(NEW_CHAT_RESOURCES.CreateGroupWith, { name: displayName })}
            </ListItemText>
          </MenuItem>
        </Menu>

        <NewGroupChatDialog
          key={channelId}
          isOpen={isCreateGroupOpen}
          onClose={() => setIsCreateGroupOpen(false)}
          onChannelCreated={openCreatedChannel}
          initialSelectedUsers={groupSeedMembers}
        />
      </>
    );
  }

  return (
    <>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
        <MenuItem onClick={onFindInConversation}>
          <ListItemIcon>
            <SearchIcon />
          </ListItemIcon>
          <ListItemText>
            {t(Resources.Chat.FindInConversation.MenuItem)}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRenameOpen}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>{t(RENAME_RESOURCES.Title)}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddMemberOpen}>
          <ListItemIcon>
            <PersonAddIcon />
          </ListItemIcon>
          <ListItemText>{t(MEMBERS_RESOURCES.AddTitle)}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMembersOpen}>
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText>{t(MEMBERS_RESOURCES.Title)}</ListItemText>
        </MenuItem>
        {!isCreator && (
          <MenuItem onClick={handleLeaveOpen}>
            <ListItemIcon>
              <ExitToAppIcon color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>
              {t(MEMBERS_RESOURCES.LeaveChannel)}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ChatMemberList
        isOpen={isMembersOpen}
        onClose={() => setIsMembersOpen(false)}
        isCreator={isCreator}
      />

      <RenameChannelDialog
        key={channelId}
        isOpen={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        channelId={channelId}
        currentName={storedChannelName}
      />

      <AddMemberDialog
        isOpen={isAddMemberOpen}
        onClose={() => setIsAddMemberOpen(false)}
        channelId={channelId}
      />

      <Dialog
        open={isLeaveConfirmOpen}
        onClose={() => setIsLeaveConfirmOpen(false)}
      >
        <DialogTitle>{t(MEMBERS_RESOURCES.LeaveConfirmTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(MEMBERS_RESOURCES.LeaveConfirmMessage)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLeaveConfirmOpen(false)}>
            {t(MEMBERS_RESOURCES.Cancel)}
          </Button>
          <Button
            onClick={handleLeaveConfirm}
            color="error"
            variant="contained"
            disabled={isLeaving}
          >
            {t(MEMBERS_RESOURCES.Leave)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
